// DASHBOARD TILE CLICK-THROUGHS — a tile opens EXACTLY the set it counted.
//
// WHY THIS FILE EXISTS. The Leads page used to fetch the right rows for the
// Interested / Callbacks / Response Watch tiles and then render something else:
// those fetches were never the page's row source, so every one of them opened the
// 100-row bootstrap with the tile's name on a banner. Measured 2026-10-07 on
// production (owner, Today): Interested tile 25 -> 100 rows; Callbacks 96 -> 40;
// Leads 40 -> 100; Daily Requests 89 -> 5,509. Nothing threw. A tile and the list
// it opens were two answers to the same question.
//
// THE RULE. The tile's click carries a SPEC (type + the window the tile counted).
// This module turns a spec into the ONE server query that returns that set, and
// the Leads page renders those rows and nothing else. The server owns every
// predicate (cohortInterested, overdue, the tile base); this file only names which
// of them a tile asks for. Module scope and pure, so verify-dashboard-matrix.js
// can RUN it against the live service and compare the count with the tile.
//
// A spec type listed here owns the Leads page's rows outright.
export var TILE_LIST_TYPES = ["periodLeads", "interested", "overdue", "responseWatch", "reportDrill"];

export function isTileList(sf) {
  return !!(sf && TILE_LIST_TYPES.indexOf(sf.type) >= 0);
}

function enc(v) { return encodeURIComponent(v == null ? "" : String(v)); }

// The server query for a tile's rows (no paging, no sort — the page appends those).
// null when the spec is not a tile list.
export function tileListUrl(sf) {
  if (!isTileList(sf)) return null;
  if (sf.type === "periodLeads") {
    return "/api/leads?tileBase=1&from=" + enc(sf.periodFrom) + "&to=" + enc(sf.periodTo) + "&fields=summary";
  }
  if (sf.type === "reportDrill") {
    // A Reports ROW's drill-down: the report's cohort (leads created in its period)
    // narrowed by the row's identity, resolved on the server with the report's own
    // grouping - ?devGroup= (development axis) / ?campaignGroup= (ad-campaign axis).
    // An EMPTY devGroup is the "No campaign / Manual" row, so it is sent, not dropped.
    // The cohort base follows the report. Performance / Strength count leads that are not
    // archived and not Daily-Request mirrors (tileBase=1). The ROI report counts every
    // non-archived lead that carries a campaign - mirrors included - so its drill asks
    // for archived=false only, or the two counts would differ by exactly the mirrors.
    var base = sf.cohort === "roi" ? "archived=false" : "tileBase=1";
    var q = "/api/leads?" + base + "&from=" + enc(sf.periodFrom) + "&to=" + enc(sf.periodTo) + "&fields=summary";
    if (sf.devGroup !== undefined && sf.devGroup !== null) q += "&devGroup=" + enc(sf.devGroup);
    if (sf.campaignGroup) q += "&campaignGroup=" + enc(sf.campaignGroup);
    if (sf.source) q += "&source=" + enc(sf.source);
    return q;
  }
  if (sf.type === "interested") {
    // The cohort (leads created in the window that ever became interested, had a
    // meeting, or closed a deal) — the tile's own closed set, closed leads included.
    return "/api/leads?cohortInterested=1&tileBase=1&from=" + enc(sf.periodFrom) + "&to=" + enc(sf.periodTo) + "&fields=summary";
  }
  if (sf.type === "overdue") {
    return "/api/leads?overdue=1&tileBase=visible&dueFrom=" + enc(sf.dueFrom) + "&dueTo=" + enc(sf.dueTo) + "&fields=summary";
  }
  // responseWatch — the id set the card counted, resolved by the card's own loader.
  return "/api/leads?responseWatch=" + enc(sf.state || "none") + "&rwFrom=" + enc(sf.rwFrom) + "&rwTo=" + enc(sf.rwTo) + "&fields=summary";
}

// The Deals tile counts APPROVED deals dated inside the period and not in the
// future (cap D4). The Deals page's Active tab also lists pending deals and has no
// future cap, so a click that only handed the page the window read 8 against a
// tile of 7 (September 2026, measured). This is the tile's row test, run by the
// page once the click set it AND by the live gate over the Deals rows.
export function dealCountsForTile(d, nowMs) {
  if (!d || d.dealApproved !== true) return false;
  var v = d.dealDate || d.eoiDate || d.updatedAt || d.createdAt;
  var at = v ? new Date(v).getTime() : NaN;
  return !isNaN(at) && at <= nowMs;
}

// THE CALLBACKS TILE, SPLIT. The tile merges two collections (leads + Daily
// Requests) into one "done / due", and its sub-line used to be one merged number
// whose click could only open the lead half — 96 opened of 213 counted (Today).
// The two halves are now two counts, each opening exactly its own list: Leads ->
// the Leads page's overdue tile list (tileListUrl type "overdue"), DR -> the Daily
// Requests page's overdue set (drTileQS, below). Pure, so the live gate runs it.
// A half the role has no book for is null, never 0.
export function callbackSplit(cb) {
  var half = function (h) { return (h && typeof h.overdue === "number") ? h.overdue : null; };
  return {
    leads: cb ? half(cb.leads) : null,
    dr: cb ? half(cb.dailyRequests) : null,
    pending: (cb && typeof cb.pending === "number") ? cb.pending : null
  };
}

// The Daily Requests list narrowing for a tile spec {type:"cbOverdue", from, to}:
// the tile's own predicate on the server (?cbOverdue=1, countDRCallbackPeriod's).
// "" for anything else, so a page with no tile spec sends nothing extra.
export function drTileQS(sf) {
  if (!sf || sf.type !== "cbOverdue") return "";
  return "&cbOverdue=1&cbFrom=" + enc(sf.from) + "&cbTo=" + enc(sf.to);
}
